import { ActionHeader } from "@/features/standard/ActionHeader";
import { Header } from "@/features/standard/Header";
import { HeaderSubTitle } from "@/features/standard/SubTitle";
import { HeaderTitle } from "@/features/standard/Title";
import { Wrapper } from "@/features/standard/Wrapper";
import styled from "@emotion/styled";
import { Badge, Button, Flex, Pagination } from "@mantine/core";
import { useEffect, useState } from "react";
import type { Column } from "react-data-grid";
import DataGrid from "react-data-grid";

interface Receipt {
  company: string;
  createdAt: string;
  serialKey: string;
}

interface ReceiptDetail {
  id: number;
  code: string;
  itemName: string;
  spec: string;
  location: string;
  lotNo: string;
  serialNumber: string;
  quantity: number;
  price: number;
  total: number;
  status: string;
  isCompleted: boolean;
}

interface ReceiptDetails {
  [key: string]: ReceiptDetail[]; // 각 공통 코드(CMM001, CMM002 등)가 key로 사용됨
}

interface ReceiptPageMockupDataType {
  receipts: Receipt[]; // 공통 코드 목록
  receiptDetails: ReceiptDetails; // 각 공통 코드에 대한 세부 항목
}

const ReceiptWrap = {
  Header: Header,
  HeaderTitle: HeaderTitle,
  HeaderSubTitle: HeaderSubTitle,
  ActionHeader: ActionHeader,
  ActionButton: Button,
  Wrapper: Wrapper,
};

export const ShipmentReceiptPage = () => {
  const [serialKey, setSerialKey] = useState<string>(""); // 초기값을 빈 문자열로 설정
  const [details, setDetails] = useState<any[]>([]); // 우측 detail 데이터를 저장하는 상태
  const [parentActivePage, setParentActivePage] = useState(1); // 좌측 테이블의 페이지 상태
  const [childActivePage, setChildActivePage] = useState(1); // 우측 테이블의 페이지 상태

  const pageSize = 10;

  // 선택된 공통 코드에 따른 필터링된 detail 데이터를 업데이트
  useEffect(() => {
    if (serialKey && ReceiptPageMockupData.receiptDetails[serialKey]) {
      setDetails(ReceiptPageMockupData.receiptDetails[serialKey]);
      setChildActivePage(1); // 새 코드 선택 시, 우측 페이지네이션 초기화
    } else {
      setDetails([]); // code가 빈 문자열이면 details는 빈 배열로 설정
    }
  }, [serialKey]);

  // 좌측 데이터의 총 갯수 및 페이지 계산
  const totalParentRows = ReceiptPageMockupData.receipts.length;
  const parentPaginatedRows = ReceiptPageMockupData.receipts.slice(
    (parentActivePage - 1) * pageSize,
    parentActivePage * pageSize,
  );

  // 우측 데이터의 총 갯수 및 페이지 계산
  const totalChildRows = details.length;
  const childPaginatedRows = details.slice(
    (childActivePage - 1) * pageSize,
    childActivePage * pageSize,
  );

  const parentColumns: readonly Column<any>[] = [
    {
      key: "company",
      name: "거래처",
      sortable: true,
      resizable: true,
    },
    {
      key: "createdAt",
      name: "입고일",
      sortable: true,
      resizable: true,
    },
    {
      key: "serialKey",
      name: "식별자",
      sortable: true,
      resizable: true,
    },
  ];

  const childColumns: readonly Column<any>[] = [
    {
      key: "id",
      name: "입고번호",
      sortable: true,
      resizable: true,
    },
    {
      key: "code",
      name: "CODE",
      sortable: true,
      resizable: true,
    },
    {
      key: "itemName",
      name: "제품명",
      sortable: true,
      resizable: true,
    },

    {
      key: "location",
      name: "로케이션",
      sortable: true,
      resizable: true,
    },
    {
      key: "lotNo",
      name: "Lot 번호",
      sortable: true,
      resizable: true,
    },
    {
      key: "serialNumber",
      name: "Serial 번호",
      sortable: true,
      resizable: true,
    },
    {
      key: "quantity",
      name: "입고량",
      sortable: true,
      resizable: true,
    },
    {
      key: "price",
      name: "단가",
      sortable: true,
      resizable: true,
    },
    {
      key: "total",
      name: "총 금액",
      sortable: true,
      resizable: true,
    },
    {
      key: "status",
      name: "테스트 필요여부",
      sortable: true,
      resizable: true,
    },
    {
      key: "status",
      name: "상태",
      sortable: true,
      resizable: true,
    },
    {
      key: "isCompleted",
      name: "입고완료가능 여부",
      sortable: true,
      resizable: true,
      formatter: ({ row }) => (
        <Badge color={row.isCompleted ? "red" : "green"}>
          {row.isCompleted ? "불가능" : "가능"}
        </Badge>
      ),
    },
  ];

  return (
    <ReceiptWrap.Wrapper>
      <ReceiptWrap.Header>
        <ReceiptWrap.HeaderTitle>입고 등록</ReceiptWrap.HeaderTitle>
        <ReceiptWrap.HeaderSubTitle>
          {"입고 의뢰 건을 선택하고, 등록할 수 있습니다."}
        </ReceiptWrap.HeaderSubTitle>
      </ReceiptWrap.Header>
      <Flex w="100%" h="100%" gap="sm" direction="column">
        {/* 왼쪽 공통코드 테이블 */}
        <GridWrapper>
          <DataGrid
            columns={parentColumns ?? []}
            rows={parentPaginatedRows ?? []}
            rowHeight={40}
            rowKeyGetter={(row) => row.serialKey}
            onRowClick={(row) => setSerialKey(row.serialKey)} // 코드 선택 시 상태 업데이트
          />
          <Pagination
            onChange={setParentActivePage} // 페이지 변경 핸들러
            value={parentActivePage}
            total={Math.ceil(totalParentRows / pageSize)} // 데이터에 따라 동적 계산
            size="lg"
            radius="sm"
            position="center"
          />
        </GridWrapper>

        {/* 우측 구분코드 테이블 (선택된 공통코드에 따라) */}
        <GridWrapper>
          {serialKey ? (
            <Flex direction="column" gap="sm" style={{ border: "1px solid black" }} p="md">
              <Flex justify="flex-end">
                {childPaginatedRows.filter((item) => !item.isCompleted).length > 0 && (
                  <Button>저장하기</Button>
                )}
              </Flex>
              <DataGrid
                columns={childColumns ?? []}
                rows={childPaginatedRows ?? []} // 선택된 코드의 detail 데이터
                rowHeight={40}
                rowKeyGetter={(row) => row.categoryCode}
              />
              <Pagination
                onChange={setChildActivePage} // 우측 페이지네이션 핸들러
                value={childActivePage}
                total={Math.ceil(totalChildRows / pageSize)} // 데이터에 따라 동적 계산
                size="lg"
                radius="sm"
                position="center"
              />
            </Flex>
          ) : (
            <p>입고 의뢰 건을 선택하세요.</p> // 코드가 선택되지 않은 경우
          )}
        </GridWrapper>
      </Flex>
    </ReceiptWrap.Wrapper>
  );
};

// 목업 데이터 정의
const ReceiptPageMockupData: ReceiptPageMockupDataType = {
  receipts: [
    { company: "성준", createdAt: "2025-06-24", serialKey: "202502176" },
    { company: "유성케미칼(주)", createdAt: "2025-06-24", serialKey: "202503101" },
    { company: "CDM케미칼", createdAt: "2025-06-24", serialKey: "202501241" },
    { company: "(주)육일씨엔에스", createdAt: "2025-06-24", serialKey: "202411272" },
    { company: "두리폴리켐", createdAt: "2025-06-25", serialKey: "202505101" },
    { company: "(주)제일CPT", createdAt: "2025-06-25", serialKey: "202505103" },
    { company: "선우플라스틱", createdAt: "2025-06-25", serialKey: "202504162" },
  ],

  receiptDetails: {
    202502176: [
      {
        id: 372,
        code: "202502176",
        itemName: "ABS",
        spec: "100",
        location: "11",
        lotNo: "20250625-035",
        serialNumber: "20250625-035",
        quantity: 2300,
        price: 1400,
        total: 3220000,
        status: "테스트 필요",
        isCompleted: true,
      },
      {
        id: 373,
        code: "202503101",
        itemName: "ABS",
        spec: "200",
        location: "11",
        lotNo: "20250625-036",
        serialNumber: "20250625-036",
        quantity: 4000,
        price: 1400,
        total: 5600000,
        status: "테스트 필요",
        isCompleted: false,
      },
    ],
    202503101: [
      {
        id: 374,
        code: "202503103",
        itemName: "PCABS",
        spec: "150",
        location: "11",
        lotNo: "LOT003",
        serialNumber: "S003",
        quantity: 150,
        price: 20,
        total: 3000,
        status: "테스트 필요 없음",
        isCompleted: false,
      },
      {
        id: 375,
        code: "202503104",
        itemName: "PCABS",
        spec: "250",
        location: "11",
        lotNo: "20250625-038",
        serialNumber: "20250625-038",
        quantity: 250,
        price: 30,
        total: 7500,
        status: "테스트 필요",
        isCompleted: false,
      },
    ],
    202501241: [
      {
        id: 376,
        code: "202505101",
        itemName: "PP",
        spec: "400",
        location: "11",
        lotNo: "20250625-039",
        serialNumber: "20250625-039",
        quantity: 3400,
        price: 450,
        total: 1530000,
        status: "테스트 필요",
        isCompleted: true,
      },
      {
        id: 377,
        code: "202505101",
        itemName: "PP",
        spec: "200",
        location: "11",
        lotNo: "20250625-040",
        serialNumber: "20250625-040",
        quantity: 500,
        price: 25,
        total: 12500,
        status: "테스트 필요",
        isCompleted: true,
      },
    ],
    202411272: [
      {
        id: 378,
        code: "202504162",
        itemName: "PP",
        spec: "100",
        location: "11",
        lotNo: "20250625-041",
        serialNumber: "20250625-041",
        quantity: 100,
        price: 10,
        total: 1000,
        status: "테스트 필요",
        isCompleted: true,
      },
      {
        id: 379,
        code: "202504171",
        itemName: "PP",
        spec: "200",
        location: "11",
        lotNo: "20250625-043",
        serialNumber: "20250625-043",
        quantity: 200,
        price: 10,
        total: 2000,
        status: "테스트 필요",
        isCompleted: true,
      },
    ],
    202505101: [
      {
        id: 380,
        code: "202504180",
        itemName: "ABS",
        spec: "200",
        location: "11",
        lotNo: "20250625-045",
        serialNumber: "20250625-045",
        quantity: 250,
        price: 100,
        total: 25000,
        status: "테스트 필요",
        isCompleted: true,
      },
      {
        id: 381,
        code: "202504181",
        itemName: "ABS",
        spec: "200",
        location: "11",
        lotNo: "20250625-046",
        serialNumber: "20250625-046",
        quantity: 250,
        price: 100,
        total: 25000,
        status: "테스트 필요",
        isCompleted: true,
      },
    ],
    202505103: [
      {
        id: 382,
        code: "202504182",
        itemName: "ABS",
        spec: "200",
        location: "11",
        lotNo: "20250625-047",
        serialNumber: "20250625-047",
        quantity: 250,
        price: 1000,
        total: 250000,
        status: "테스트 필요",
        isCompleted: true,
      },
    ],
    202504162: [
      {
        id: 383,
        code: "202504183",
        itemName: "ABS",
        spec: "300",
        location: "11",
        lotNo: "20250625-048",
        serialNumber: "20250625-048",
        quantity: 300,
        price: 100,
        total: 30000,
        status: "테스트 필요",
        isCompleted: true,
      },
      {
        id: 384,
        code: "202504184",
        itemName: "PP",
        spec: "300",
        location: "11",
        lotNo: "20250625-050",
        serialNumber: "20250625-050",
        quantity: 200,
        price: 20,
        total: 4000,
        status: "테스트 필요",
        isCompleted: true,
      },
    ],
  },
};

const GridWrapper = styled.div`
  & *[role="grid"] {
    height: 440px;
    // --rdg-background-color: {(props) => (props.isDarkMode ? "white" : "none")};
    // --rdg-header-background-color: {(props) =>
    // props.isDarkMode ? "white" : "none"};
    // --rdg-color: {(props) => (props.isDarkMode ? "black" : "white")};
    // --rdg-row-hover-background-color: {(props) =>
    // props.isDarkMode ? "#f5f5f5" : theme?.colors?.gray?.[7]};
  }
  & *[role="columnheader"] {
    // color: #7d8fa9;
    font-size: 12px;
    font-family: Roboto;
    font-weight: 500;
    word-wrap: break-word;
    // border: none;
    box-shadow: none;
    display: flex;
    align-items: center;
  }
  & *[aria-colindex="1"] {
  }

  & *[role="row"] {
    height: 100px;
  }

  & *[role="gridcell"] {
    display: flex;
    align-items: center;
    // border-left: none;
    // border-right: none;
    box-shadow: none;

    & > * {
      justify-content: flex-start;
    }
  }
  display: flex;
  flex-direction: column;
  width: 100%;
  justify-content: space-between;
  gap: 1rem;
`;

const OptionBox = styled.fieldset`
  display: flex;
  gap: 10px;
  border: none;
`;

const OptionBtn = styled<any>(Button)`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
